import { theme } from '@Guidelines/Theme/theme';
import DocumentRowActions from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Sections/StagesSection/StageCard/DocumentRow/DocumentRowActions';
import type DocumentSettingsFormData from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/DocumentSettingsFormData';
import describeDocument from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Sections/StagesSection/StageCard/DocumentRow/Helpers/describeDocument';
import useWorkflowDraft from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Providers/WorkflowDraftProvider/useWorkflowDraft';
import { css } from '@emotion/react';
import { FileText } from 'lucide-react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';

const rowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.875rem;
	flex-wrap: wrap;
	padding: 0.875rem 1rem;
	border-radius: ${theme.radii.small};
	background-color: ${theme.colors.surface};
`;

const iconStyle = css`
	display: flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: 2.25rem;
	height: 2.25rem;
	border-radius: 0.75rem;
	background-color: ${theme.colors.accentSurface};
	color: ${theme.colors.accentDark};
`;

const textStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.1875rem;
	flex: 1;
	min-width: 13.75rem;
`;

const labelStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.90625rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.35;
	color: ${theme.colors.ink};
`;

const summaryStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly stageIndex: number;
	readonly stepIndex: number;
	readonly document: DocumentSettingsFormData;
};

/** One document of a stage: its label, a summary of how it is produced and what it receives, and its actions. */
const DocumentRow: React.FC<Props> = ({ stageIndex, stepIndex, document }) => {
	const { draft } = useWorkflowDraft();
	const documentTypes = useSelector((state) => state.documentTypesStore.documentTypes);

	const typeLabel =
		document.documentTypeId !== null
			? (documentTypes?.labelOf(document.documentTypeId) ?? 'Type inconnu')
			: 'Type inconnu';
	const label = document.label.trim() === '' ? typeLabel : document.label;

	const summary = React.useMemo(
		() => describeDocument(document, draft, stageIndex),
		[document, draft, stageIndex],
	);

	return (
		<div css={rowStyle}>
			<div css={iconStyle}>
				<FileText size={18} aria-hidden="true" />
			</div>
			<div css={textStyle}>
				<span css={labelStyle}>{label}</span>
				<span css={summaryStyle}>{summary}</span>
			</div>
			<DocumentRowActions stageIndex={stageIndex} stepIndex={stepIndex} />
		</div>
	);
};

export default DocumentRow;
